/**
 * WordPress dependencies.
 */
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies.
 */
import { RazorpayConstructor } from '../types';
import { getRazorpayMethodLabel as getRazorpayMethodLabelEn } from './razorpay-method-meta';

export { RAZORPAY_METHOD_META, getRazorpayMethodIcon } from './razorpay-method-meta';

/**
 * Razorpay Checkout JS SDK URL.
 */
export const RAZORPAY_CHECKOUT_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

// Kept here so the script translation JSON, filtered by JS source reference, carries these msgids.
const LOCALIZED_LABELS: Record<string, string> = {
  card: __('Credit Card', 'surecart'),
  upi: __('UPI', 'surecart'),
};

/**
 * Localized label for a Razorpay method id, or `undefined` for unknown ids.
 */
export const getRazorpayMethodLabel = (type?: string | null): string | undefined => {
  const label = getRazorpayMethodLabelEn(type);
  if (!label) return undefined;
  return LOCALIZED_LABELS[type as string] ?? label;
};

/**
 * Cached Razorpay instance and load promise for singleton pattern.
 */
let razorpayInstance: RazorpayConstructor | null = null;
let loadPromise: Promise<RazorpayConstructor> | null = null;

/**
 * Load Razorpay SDK script.
 * Uses singleton pattern to avoid loading the script multiple times.
 *
 * @returns Promise that resolves to the Razorpay constructor.
 */
export const loadRazorpay = (): Promise<RazorpayConstructor> => {
  if (razorpayInstance) {
    return Promise.resolve(razorpayInstance);
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      razorpayInstance = (window as any).Razorpay;
      resolve(razorpayInstance);
    };
    script.onerror = () => {
      loadPromise = null; // Reset so it can be retried.
      reject(new Error(__('Failed to load Razorpay script.', 'surecart')));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
};
